import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ChevronRight, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTranslation } from 'react-i18next';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_46013b23-b5fe-4779-b886-ac2171337e18/artifacts/nmw6zqxw_GCI%20Logo-%20Transparent%20-%20Remove_background_from_this_image_make_it_transpa-1771909885908.png";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const isActive = (path) => location.pathname === path;

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'es' : 'en';
    i18n.changeLanguage(newLang);
    localStorage.setItem('gci_language', newLang);
  };

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.about'), path: '/about' },
    { 
      name: t('nav.services'), 
      path: '/services',
      dropdown: [
        { name: t('nav.allServices'), path: '/services' },
        { name: t('nav.workforce'), path: '/services/workforce' },
        { name: t('nav.volunteerService'), path: '/services/volunteer' },
        { name: t('nav.collaboration'), path: '/services/collaboration' },
        { name: t('nav.health'), path: '/services/health' },
        { name: t('nav.housing'), path: '/services/housing' },
      ]
    },
    { 
      name: t('nav.calendarTitle'), 
      path: '/impact-calendar',
    },
    {
      name: t('nav.volunteerHub'),
      path: '/volunteer',
      dropdown: [
        { name: t('nav.findOpportunities'), path: '/volunteer' },
        { name: t('nav.volunteerLeaderboard'), path: '/volunteer/leaderboard' },
        { name: t('nav.becomeVolunteer'), path: '/volunteer/register' },
        { name: t('nav.registerOrg'), path: '/volunteer/org-register' },
        { name: t('nav.corporateTeams'), path: '/corporate/register' },
        { name: t('nav.volunteerLogin'), path: '/volunteer/login' },
        { name: t('nav.orgLogin'), path: '/partner/login' },
      ]
    },
    { name: t('nav.events'), path: '/events' },
    { 
      name: t('nav.community'),
      path: '/news',
      dropdown: [
        { name: t('nav.blog'), path: '/blog' },
        { name: t('nav.news'), path: '/news' },
        { name: t('nav.testimonials'), path: '/testimonials' },
        { name: t('nav.resources'), path: '/resources' },
        { name: t('nav.instructors'), path: '/instructors' },
        { name: t('nav.sponsorship'), path: '/sponsorship' },
        { name: t('nav.appointments'), path: '/appointments' },
      ]
    },
    { name: t('nav.contact'), path: '/contact' },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20 md:h-32">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="header-logo-link">
            <img 
              src={LOGO_URL} 
              alt="Greater Community Impact" 
              className="h-16 md:h-28 w-auto"
              data-testid="header-logo"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-0.5" data-testid="desktop-nav">
            {navLinks.map((link) => (
              link.dropdown ? (
                <DropdownMenu key={link.name}>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className={`flex items-center px-3 py-2 text-sm font-medium transition-colors rounded-md whitespace-nowrap ${
                        location.pathname.startsWith('/services') 
                          ? 'text-brand-blue' 
                          : 'text-brand-text-dark hover:text-brand-blue'
                      }`}
                      data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                      <ChevronDown className="ml-1 h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    {link.dropdown.map((subLink) => (
                      <DropdownMenuItem key={subLink.path} asChild>
                        <Link 
                          to={subLink.path}
                          className="w-full cursor-pointer"
                          data-testid={`nav-dropdown-${subLink.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {subLink.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`px-3 py-2 text-sm font-medium transition-colors rounded-md whitespace-nowrap ${
                    isActive(link.path) 
                      ? 'text-brand-blue' 
                      : 'text-brand-text-dark hover:text-brand-blue'
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </nav>

          {/* Donate + Language */}
          <div className="hidden lg:flex items-center gap-2">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-brand-text-dark hover:text-brand-blue rounded-md transition-colors"
              data-testid="language-switcher"
              aria-label="Switch language"
            >
              <Globe className="h-4 w-4" />
              {i18n.language === 'en' ? 'ES' : 'EN'}
            </button>
            <Link to="/donate">
              <Button 
                className="bg-brand-orange hover:bg-brand-orange-hover text-white font-semibold px-6 py-2 rounded-md shadow-md transition-all"
                data-testid="donate-btn"
              >
                {t('nav.donate')}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-3 min-h-[44px] min-w-[44px] rounded-md text-brand-text-dark hover:bg-gray-100 active:bg-gray-200 transition-colors flex items-center justify-center"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-2 border-t animate-in slide-in-from-top-2 duration-200 max-h-[calc(100vh-5rem)] overflow-y-auto" data-testid="mobile-menu">
            <nav className="flex flex-col" role="navigation" aria-label="Mobile navigation">
              {navLinks.map((link) => (
                link.dropdown ? (
                  <div key={link.name} className="border-b border-gray-100 last:border-b-0">
                    <button
                      className="flex items-center justify-between w-full px-4 py-3.5 min-h-[48px] text-sm font-semibold text-brand-text-dark hover:bg-gray-50 active:bg-gray-100 transition-colors"
                      onClick={() => setExpandedSection(expandedSection === link.name ? null : link.name)}
                      data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                      aria-expanded={expandedSection === link.name}
                    >
                      {link.name}
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${expandedSection === link.name ? 'rotate-180' : ''}`} />
                    </button>
                    <div className={`overflow-hidden transition-all duration-200 ${expandedSection === link.name ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                      <div className="bg-gray-50 py-1">
                        {link.dropdown.map((subLink) => (
                          <Link
                            key={subLink.path}
                            to={subLink.path}
                            className={`flex items-center gap-2 px-6 py-3 min-h-[44px] text-sm transition-colors ${
                              location.pathname === subLink.path
                                ? 'text-brand-blue bg-blue-50 font-medium'
                                : 'text-gray-600 hover:bg-gray-100 active:bg-gray-200'
                            }`}
                            onClick={() => setMobileMenuOpen(false)}
                            data-testid={`mobile-subnav-${subLink.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <ChevronRight className="h-3 w-3 text-gray-400 flex-shrink-0" />
                            {subLink.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`flex items-center px-4 py-3.5 min-h-[48px] text-sm font-semibold border-b border-gray-100 last:border-b-0 transition-colors ${
                      isActive(link.path)
                        ? 'text-brand-blue bg-blue-50'
                        : 'text-brand-text-dark hover:bg-gray-50 active:bg-gray-100'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
              <div className="px-4 pt-4 pb-2 space-y-2 border-t border-gray-200 mt-1">
                <Link
                  to="/donate"
                  className="block py-3 min-h-[48px] text-center text-white font-semibold bg-brand-orange hover:bg-brand-orange-hover active:bg-[#c45e15] rounded-lg transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-donate-btn"
                >
                  {t('nav.donate')}
                </Link>
                <button
                  onClick={() => { toggleLanguage(); setMobileMenuOpen(false); }}
                  className="flex items-center justify-center gap-2 w-full py-3 min-h-[48px] text-sm font-medium text-brand-text-dark bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  data-testid="mobile-language-switcher"
                >
                  <Globe className="h-4 w-4" />
                  {i18n.language === 'en' ? 'Cambiar a Espa\u00f1ol' : 'Switch to English'}
                </button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
